/**
 * File-object transfers hit two different kinds of URL depending on
 * environment: an ABSOLUTE presigned R2 URL (staging/production) that must be
 * fetched with a bare request — attaching the app's Authorization header makes
 * R2 reject it, since a presigned URL already carries its auth in the query —
 * or a RELATIVE Worker-proxied path (local dev) that needs the bearer token.
 *
 * The server decides which it returns; the client just routes accordingly.
 */

type AuthFetch = (path: string, init?: RequestInit) => Promise<Response>

export function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url)
}

/** Plain fetch for absolute (presigned) URLs; authFetch for relative API paths. */
export function objectFetch(authFetch: AuthFetch, url: string, init?: RequestInit): Promise<Response> {
  return isAbsoluteUrl(url) ? fetch(url, init) : authFetch(url, init)
}

/**
 * Base64 SHA-256 of the bytes about to be uploaded (audit FF-08).
 *
 * The server signs this into the presigned PUT and the client sends it back as
 * `x-amz-checksum-sha256`, so R2 verifies the body against it and reports the
 * digest from `head()` — which is what finally populates
 * `attachments.checksum_sha256` on the direct-to-R2 path. Without it that
 * column is NULL for every file uploaded in staging and production, because the
 * bytes never pass through the Worker that would otherwise hash them.
 *
 * Returns undefined rather than throwing when WebCrypto is unavailable (very
 * old WebViews, or a non-secure context). The whole feature is optional
 * end-to-end: no checksum declared ⇒ the server signs exactly as before ⇒ the
 * upload behaves exactly as it did. A capture must never fail over a digest.
 */
export async function sha256Base64(bytes: ArrayBuffer): Promise<string | undefined> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    let binary = ''
    for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte)
    return btoa(binary)
  } catch {
    return undefined
  }
}
